import { OpportunityStage } from "../types";
import { ExpertPersona, ModalKind } from "../components/chat/types";

export type StagePrompt = {
  expert: ExpertPersona;
  text: string;
  modalSuggestion?: ModalKind;
};

const stagePrompts: Record<OpportunityStage, StagePrompt> = {
  intake_started: {
    expert: "Intake Scout",
    text: "Let's build this play from the ground up. Start by dropping your resume — paste the text directly here, or use the attach button to upload the file. If you already have the job description, add that next.",
    modalSuggestion: "add-proof",
  },
  intake_complete: {
    expert: "Resume Architect",
    text: "Proof is in. Now let's make sure your profile reads sharp. I've pulled what I can from your documents — review it and correct anything that doesn't sound like you. Gaps included: calling them out now saves trouble later.",
    modalSuggestion: "edit-profile",
  },
  fit_review: {
    expert: "Alignment Mapper",
    text: "Time for the fit check. I'm going to compare your background against the role requirements. I'll surface the strong matches, flag the gaps, and give you an honest confidence score. If you haven't added the job description yet, drop it now and I'll do the analysis.",
    modalSuggestion: "add-proof",
  },
  positioning: {
    expert: "Resume Architect",
    text: "Your story needs to be sharp before anything goes out. Let's tailor your resume to this specific role — I'll highlight what to move up, what to cut, and which keywords the ATS is looking for. We'll version it so nothing gets lost.",
  },
  outreach_ready: {
    expert: "Outreach Coordinator",
    text: "Drafts are almost ready to send. Let's review the message before it goes out — tone, timing, and whether the subject line actually earns the open. I can also help you capture the recruiter's contact info so nothing gets buried.",
    modalSuggestion: "compose-dm",
  },
  interview_active: {
    expert: "Interview Coach",
    text: "You're in the interview phase. I've pulled the common interview formats for this type of role. Tell me which round you're prepping for and I'll give you the question patterns, what they're actually testing, and how to structure your answers.",
  },
  debrief_captured: {
    expert: "Debrief Analyst",
    text: "Let's debrief while it's fresh. Walk me through how it went — or paste the transcript if you recorded it. I'll score your performance, flag what landed, what missed, and what you shouldn't repeat in round two.",
  },
  offer_review: {
    expert: "Offer Strategist",
    text: "Offer is on the table. Drop the details — base, equity, bonus, benefits — and I'll run the analysis. I'll tell you where it sits in the market, what's missing that's common for this role, and whether you have leverage to push back.",
    modalSuggestion: "add-proof",
  },
  closed_won: {
    expert: "System",
    text: "You won this one. The play is closed and the record is preserved. Take a minute to log your takeaways — what worked, what you'd do differently. That becomes your playbook for the next one.",
  },
  closed_lost: {
    expert: "System",
    text: "This one didn't land. That's part of the game. The record is here if you ever want to revisit. Log what you learned and carry it to the next play.",
  },
};

export function useStageDirector(currentStage: OpportunityStage | undefined): StagePrompt {
  const stage = currentStage ?? "intake_started";
  return stagePrompts[stage];
}

export function getStagePrompt(stage: OpportunityStage): StagePrompt {
  return stagePrompts[stage];
}
